import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { customerApi } from "@repair/api";
import { formatDateTime } from "@repair/contracts";
import { Button, EmptyState, LoadingState, PrimaryActionHint, RowLink, StatusBadge } from "@repair/ui";

const segments = [
  { key: "action", label: "待我处理" },
  { key: "active", label: "处理中" },
  { key: "closed", label: "已完成" },
] as const;

export function OrdersPage() {
  const navigate = useNavigate();
  const filter = new URLSearchParams(window.location.search).get("view") || "active";
  const orders = useQuery({ queryKey: ["customer-orders"], queryFn: () => customerApi.list() });
  const items = (orders.data?.items || []).filter((item) => {
    if (filter === "action") return item.waiting_on === "customer";
    if (filter === "closed") return ["CLOSED", "CANCELLED"].includes(item.state_code);
    return !["CLOSED", "CANCELLED"].includes(item.state_code) && item.waiting_on !== "customer";
  });

  return (
    <div className="customer-page">
      <header className="customer-page-title">
        <div><h1>我的返修</h1><p>{orders.data ? `${orders.data.total} 张工单` : "工单进度与待办"}</p></div>
        <Button onClick={() => navigate("/new")}><Plus size={16} />新建</Button>
      </header>
      <div className="customer-segments" role="tablist" aria-label="工单分类">
        {segments.map((segment) => (
          <button
            key={segment.key}
            role="tab"
            aria-selected={filter === segment.key}
            onClick={() => navigate(`/orders?view=${segment.key}`, { replace: true })}
          >
            {segment.label}
          </button>
        ))}
      </div>
      {orders.isLoading && <LoadingState label="正在读取工单" />}
      {orders.error && <EmptyState title="工单加载失败" detail={orders.error.message} action={<Button onClick={() => void orders.refetch()}>重试</Button>} />}
      {!orders.isLoading && !orders.error && items.length === 0 && (
        <EmptyState
          title={filter === "action" ? "目前没有需要你处理的工单" : filter === "closed" ? "暂无已完成工单" : "暂无处理中工单"}
          detail="工单状态变化后会自动出现在对应分类。"
        />
      )}
      <div className="order-card-list">
        {items.map((item) => (
          <Link to={`/orders/${encodeURIComponent(item.id)}`} className="order-card" key={item.id}>
            <div className="order-card__top">
              <StatusBadge label={item.state?.label || item.current_status} tone={item.state?.tone || toneFor(item.state_code, item.waiting_on)} />
              <code>{item.order_no}</code>
            </div>
            <div>
              <h2>{item.sn || "未填写设备 SN"}</h2>
              <div className="order-card__meta"><strong>{item.fault_desc}</strong></div>
            </div>
            {item.waiting_on === "customer" && <div className="order-card__action"><PrimaryActionHint label="需要你处理" /></div>}
            <div className="order-card__foot"><span>{formatDateTime(item.updated_at)} 更新</span><RowLink>查看详情</RowLink></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function toneFor(state: string, waitingOn: string | null) {
  if (["CLOSED", "CUSTOMER_RECEIVED"].includes(state)) return "success" as const;
  if (state === "CANCELLED") return "neutral" as const;
  if (waitingOn === "customer") return "attention" as const;
  return "info" as const;
}
